import * as Sentry from '@sentry/react'

// Patterns that indicate a client-side or network failure outside our control.
// These are not actionable server-side bugs and should not be reported.
const IGNORED_ERROR_PATTERNS = [
  // Fetch/XHR network failures (no internet, DNS, timeout, CORS preflight)
  /Issue connecting to server/i,
  /Failed to fetch/i,
  /NetworkError/i,
  /Network request failed/i,
  /Load failed/i,
  // Browser extension interference
  /ResizeObserver loop limit exceeded/i,
  // Well-documented noise from browser accessibility/legacy-IE-mode extensions
  // (e.g. Windows Narrator, older Edge/IE extensions) reporting failed internal
  // messaging. Unrelated to app code.
  /Object Not Found Matching Id/i,
  // React DOM commit-phase DOM mutation conflicts, almost always caused by a
  // browser extension (Google Translate, ad blockers, password managers)
  // mutating the DOM outside of React's control. Not fixable from app code.
  /Failed to execute 'removeChild' on 'Node'/i,
  /Failed to execute 'insertBefore' on 'Node'/i,
  // Safari/iOS quirks
  /TypeError: cancelled/i,
]

function isNetworkOrClientError (event) {
  const exceptions = event?.exception?.values ?? []

  // Safari/iOS rejects cancelled fetch() promises with a DOM Event object
  // instead of an Error. This produces no stack trace and no actionable message.
  const isSafariEventRejection = exceptions.some(
    ({ value, mechanism }) =>
      /captured as promise rejection/i.test(value ?? '') &&
      mechanism?.handled === false &&
      mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
  )
  if (isSafariEventRejection) return true

  return exceptions.some(({ type, value }) => {
    const message = `${type ?? ''}: ${value ?? ''}`
    return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  })
}

// Some global window.onerror/unhandledrejection events carry no actionable
// information at all: the "value" is a short opaque token (an internal
// constructor/variable name from a third-party or cross-origin script, e.g.
// GA/GTM's injected snippets), there is no stack frame pointing at one of our
// own built JS assets, and - tellingly - the reported "filename" is the
// current page's own URL path rather than any script file. This is the
// classic signature of a cross-origin script error the browser redacted the
// real source for (the "Script error." problem). These are not actionable
// from app-web code.
function isOpaqueThirdPartyGlobalError (event) {
  const exceptions = event?.exception?.values ?? []
  return exceptions.some(({ value, mechanism, stacktrace }) => {
    if (mechanism?.handled !== false) return false
    if (
      mechanism?.type !== 'auto.browser.global_handlers.onerror' &&
      mechanism?.type !== 'auto.browser.global_handlers.onunhandledrejection'
    ) {
      return false
    }
    const frames = stacktrace?.frames ?? []
    const hasRealSourceFrame = frames.some(
      (frame) => frame.filename && /\.[jt]sx?(\?|$)/i.test(frame.filename)
    )
    if (hasRealSourceFrame) return false
    return !value || (value.length <= 24 && !/\s/.test(value))
  })
}

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://7a8ec6ce3174f2a0f286316a57c7aa6e@o117841.ingest.us.sentry.io/4509987650076672',
    release: APP_VERSION,
    environment: String(import.meta.env.VITE_APP_ENV),
    beforeSend (event) {
      if (isNetworkOrClientError(event) || isOpaqueThirdPartyGlobalError(event)) {
        return null
      }
      return event
    },
  })
}
